// /////////////////////////////////////////////////////////////
// [ファイル] ProfitAndLossAiReviewCard.tsx
// [概要] 経営概況詳細 (損益) ページの「AI 評価分析」カード。
//        ワンクリックで当期業績 (全社サマリー・部署別・異常科目) を AI が評価し、
//        レポート (Markdown) として画面内に表示する。深掘りはチャットモーダルへ誘導する。
// /////////////////////////////////////////////////////////////
import React from 'react';
import { Box, Card, CardContent, Typography, Button, Alert } from '@mui/material';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import MarkdownMessage from '../MarkdownMessage';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../../constants/buttonStyles';
import {
  PERFORMANCE_RANGE_OPTIONS,
  type PerformanceRange,
  type TrialBalanceRow,
  type DepartmentPlRow,
  type BranchPlRow,
  type StaffHeadcountRow,
} from './constants';
import { useProfitAndLossReview } from '../../hooks/useProfitAndLossReview';

type Props = {
  dedupedRows: TrialBalanceRow[];
  deptPlRows: DepartmentPlRow[];
  branchPlRows: BranchPlRow[];
  staffRows: StaffHeadcountRow[];
  performanceRange: PerformanceRange;
  currentMonth: number;
  fiscalYear: number;
  planHiresByMonth?: Record<number, number>;
  planVersionName?: string;
};

/**
 * AI 評価分析カード。業績サマリー直上に配置し、ワンクリックで評価レポートを生成する。
 */
export default function ProfitAndLossAiReviewCard({
  dedupedRows,
  deptPlRows,
  branchPlRows,
  staffRows,
  performanceRange,
  currentMonth,
  fiscalYear,
  planHiresByMonth,
  planVersionName,
}: Props) {
  const { loading, error, result, generate } = useProfitAndLossReview();

  const rangeLabel =
    performanceRange === 'month'
      ? `${currentMonth}月`
      : PERFORMANCE_RANGE_OPTIONS.find((o) => o.value === performanceRange)?.label ?? '通期';

  const handleGenerate = () => {
    void generate({ dedupedRows, deptPlRows, branchPlRows, staffRows, performanceRange, currentMonth, fiscalYear, planHiresByMonth, planVersionName });
  };

  return (
    <Card sx={{ width: '100%', borderRadius: '10px', boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.08)', border: '1px solid #f1f5f9' }}>
      <CardContent sx={{ px: 2.5, py: 1.75, '&:last-child': { pb: 2 } }}>
        {/* ヘッダー (コンパクト) */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: result || loading ? 1.5 : 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', p: 0.6, borderRadius: '8px', backgroundColor: '#eff6ff', color: '#3b82f6' }}>
              <Icons.AutoAwesome sx={{ fontSize: '18px' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                AI 評価分析
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                {fiscalYear}年度 {rangeLabel}の業績・拠点別・部署別・異常科目・従業員数・採用予実・生産性を AI が評価します
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {result && (
              <Button
                onClick={handleGenerate}
                disabled={loading}
                startIcon={<Icons.Refresh />}
                sx={STANDARD_TEXT_BUTTON_SX}
              >
                再生成
              </Button>
            )}
            {!result && (
              <Button
                onClick={handleGenerate}
                disabled={loading}
                startIcon={<Icons.AutoAwesome />}
                sx={STANDARD_CTA_BUTTON_SX}
              >
                AI で評価する
              </Button>
            )}
          </Box>
        </Box>

        {error && <Alert severity="warning" sx={{ borderRadius: '8px', mb: result ? 1.5 : 0 }}>{error}</Alert>}

        {loading && <LoadingView message="AI 分析中..." height="20vh" size={50} />}

        {result && !loading && (
          <Box sx={{ borderTop: '1px solid #f1f5f9', pt: 1.5 }}>
            <MarkdownMessage>{result}</MarkdownMessage>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
